import { useEffect, useState } from "react";

import { useForm } from "react-hook-form";
import useNotification from "../notifications/useNotifications";

import Modal from "../../layouts/Modal";
import { TextInput } from "../shared/Inputs/TextInput";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import LoadingWheel from "../shared/LoadingWheel";

import useWilmaUsers from "../../data/useWilmaUsers";
import useCustomerRoles from "../../data/useCustomerRoles";
import { useAuth } from "../../contexts/AuthContext";
import { IWilmaUser } from "../../models/auth/WilmaUser";
import RoleSelect, { CUSTOM_ROLE, roleValue, userRoleValue } from "./RoleSelect";

type FormValues = {
    forename: string;
    surname: string;
    email: string;
};

export default function AdminUserEditModal({ open, setOpen, user }: { open: boolean, setOpen: React.Dispatch<React.SetStateAction<boolean>>, user: IWilmaUser | undefined }) {
    const { addNotification } = useNotification();
    const { userId } = useAuth();
    const { updateWilmaUser } = useWilmaUsers();
    const { roles } = useCustomerRoles();
    const customerRoles = roles.data?.data ?? [];
    const [selectedRoleId, setSelectedRoleId] = useState<string>(CUSTOM_ROLE);
    const [error, setError] = useState<string | null>(null);

    const { handleSubmit, register, reset } = useForm<FormValues>();

    const isSelf = user?.id === userId;
    const isOnKnownRole = customerRoles.some((r) => roleValue(r) === selectedRoleId);
    const startedOnCustom = !!user && !customerRoles.some((r) => roleValue(r) === userRoleValue(user));

    useEffect(() => {
        if (!open || !user) return;

        reset({ forename: user.forename, surname: user.surname, email: user.email });
        setSelectedRoleId(userRoleValue(user));
        setError(null);
    }, [open, user, reset]);

    const onSubmit = (data: FormValues) => {
        if (!user) return;

        if (!data.forename.trim() || !data.surname.trim()) {
            setError("Please enter a first and last name");
            return;
        }

        setError(null);

        updateWilmaUser.mutate({
            userId: user.id,
            forename: data.forename,
            surname: data.surname,
            // Custom means "leave their access as it is" - the widget can't edit individual scopes
            roleId: isOnKnownRole ? selectedRoleId : null,
        }, {
            onSuccess: (res: any) => {
                setOpen(false);
                addNotification({
                    variant: "success",
                    primaryText: res?.message || "User saved",
                });
            },
            onError: (err: any) => {
                setError(err?.message || "Failed to save user");
            }
        });
    }

    return (
        <Modal width="max-w-2xl" open={open} setOpen={setOpen} >
            <h1 className="text-xl">Edit User</h1>
            <div className="mt-4">
                {roles.isLoading ? (
                    <LoadingWheel />
                ) : (
                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 border-t border-gray-200">
                        <div className="pt-4 grid grid-cols-1 gap-x-4 gap-y-4 sm:gap-y-6 sm:grid-cols-2">
                            <TextInput label="First Name" registerName={"forename"} register={register}/>
                            <TextInput label="Last Name" registerName={"surname"} register={register}/>
                            <TextInput label="Email" disabled={true} registerName={"email"} register={register}/>

                            <RoleSelect
                                roles={customerRoles}
                                value={selectedRoleId}
                                onChange={setSelectedRoleId}
                                disabled={isSelf}
                                showCustom={startedOnCustom}
                                hint={roles.isError ? "Couldn't load roles." : isSelf ? "You can't change your own role." : undefined}
                            />
                        </div>
                        {error && <p className="text-sm text-red-500">{error}</p>}
                        <ButtonInput label={updateWilmaUser.isLoading ? "Saving..." : "Save"} isSubmit disabled={updateWilmaUser.isLoading} />
                    </form>
                )}
            </div>
        </Modal>
    )
}
